/**
 * EventDetailTemplate — the event detail page, as components.
 *
 * A faithful port of the Page Builder record template of the same name: same
 * markup, same class names, same stylesheet (styles/eventDetail.css, exported
 * verbatim). What changes is only WHO renders it — React components with real
 * props, rather than an HTML string walked by renderRecordTemplate.
 *
 * It takes the scope the renderer would have been given — `{ event, categories,
 * registration, organiser, sponsors }`, keyed exactly as the related blocks
 * arrive — so the data contract is unchanged and nothing here fetches anything.
 * EventDetailPage owns the data and the actions; this owns the layout.
 */

import React from 'react';
import '../../../styles/eventDetail.css';
// The eight typography tokens an organizer can apply to a description. Shared
// with OrgRichText so the editor and this page render them identically.
import '../../../styles/richText.css';

import EventHero from './EventHero';
import EventFacts from './EventFacts';
import EventAbout from './EventAbout';
import EventCategories from './EventCategories';
import EventProseSection from './EventProseSection';
import EventRegistrationSection from './EventRegistrationSection';
import EventOrganiser from './EventOrganiser';
import EventSponsors from './EventSponsors';
import EventTitleSponsor from './EventTitleSponsor';
import EventRail from './EventRail';
import EventStickyBar from './EventStickyBar';
import { createFormatter, formatValue, isTruthy, readValue, safeUrl } from './eventDetailScope';
import { summariseFee, totalPlaces } from './activityPricing';
import { buildShareMessage } from './shareMessage';
import { formatAmount } from '../currencyCatalog';
// One definition of the public event URL, shared with the organiser console.
import { eventShareUrl } from '../../organizer/events/EventShareLink';

export default function EventDetailTemplate({
  scope, recordAlias = 'event',
  buttons,
  isCompleted, onAction, pendingPayment, onBookMore,
  // Resolved by the page and handed down, rather than fetched here. This is a
  // presentational component: giving it its own query would make every test
  // that renders it need a QueryClientProvider, and would fetch the catalog a
  // second time for a value the page already holds.
  currency = null,
  // The participant's own check-in section, built by the page because it needs
  // the session and the write path. A node rather than data: this component
  // stays presentational, and a page that passes nothing renders exactly as it
  // did before the section existed.
  checkIn = null,
}) {
  const fmt = createFormatter(scope, recordAlias);

  const registration = readValue(scope, 'registration');
  const organiser = readValue(scope, 'organiser');
  const activities = readValue(scope, 'categories');
  const eventId = readValue(scope, `${recordAlias}.id`);


  // Fee and places now live on the ACTIVITY, not on the event's registration
  // row. The rail has space for one of each, so it states the cheapest way in
  // and the total capacity — see activityPricing.js for why.
  const fee = summariseFee(activities, {
    fallbackIsPaid: isTruthy(readValue(scope, `${recordAlias}.is_payment_required`)),
  });
  const isPaid = fee.isPaid;
  // No activity states a fee: nothing to print. `isPaid` is then the event's
  // own flag, so the card still reads "Free" rather than an empty price.
  const feeAmount = fee.amount === null
    ? ''
    : `${fee.isFrom ? 'From ' : ''}${formatAmount(fee.amount, currency)}`;

  const capacity = totalPlaces(activities);

  // How entries are governed. `registration_type` is a joined lookup row, so
  // its name is used when the source sends one and the row's own id is never
  // shown — "1" tells a visitor nothing about whether they may enter.
  const registrationTypeRow = readValue(scope, 'registration_type');
  const registrationType = registrationTypeRow
    ? (registrationTypeRow.name || registrationTypeRow.label || '')
    : '';
  // `false` is the meaningful value here, so an event that simply does not say
  // must not be reported as "not required" — hence the explicit comparison.
  const registrationRequiredRaw = readValue(scope, `${recordAlias}.is_registration_required`);
  const registrationRequired = registrationRequiredRaw === undefined
    ? undefined : Boolean(registrationRequiredRaw);
  const externalUrl = safeUrl(readValue(scope, 'registration.external_url'));

  // "IST" / "BST" / "GMT+5:30", spelled in the reader's locale but naming the
  // EVENT's zone. A published start time without one is ambiguous the moment
  // the event is not in the reader's own country.
  const timeZoneLabel = fmt(`${recordAlias}.start_datetime`, 'zone');

  const opensAt = fmt('registration.registration_start_datetime', 'datetime');
  const closesAt = fmt('registration.registration_end_datetime', 'datetime');
  const places = capacity === null ? '' : formatValue(capacity, 'number');
  const organiserWebsiteUrl = safeUrl(readValue(scope, 'organiser.official_website_url'));

  return (
    <div className="evb">

      <div className="evb-zone">

        {/* ABOVE THE HERO, and only here. A line saying check-in is open, which
            opens a dialog — one placement for both widths, rather than a copy
            in the rail and another in the page. It draws nothing at all when
            there is nothing to check in to. */}
        {checkIn}

        <EventHero
          status={fmt(`${recordAlias}.status`)}
          title={fmt(`${recordAlias}.title`)}
          coverImageUrl={safeUrl(readValue(scope, `${recordAlias}.cover_image_url`))}
          location={fmt(`${recordAlias}.location`)}
          startDatetime={fmt(`${recordAlias}.start_datetime`, 'datetime')}
          timeZoneLabel={timeZoneLabel}
          // The link the share control hands out. Built from the SAME helper
          // the organiser console's "Event link" field uses, so what a visitor
          // shares and what an organiser copies are one URL with one
          // definition — and it is the URL the Android app shares too.
          shareUrl={eventId == null || eventId === '' ? null : eventShareUrl(eventId)}
          // Fixed copy for every event — see shareMessage.js for why it does
          // not name the sport. Called rather than inlined so the wording has
          // one home.
          shareMessage={buildShareMessage()}
        />

        <div className="evb-grid">
          <div className="evb-main">

            {/* The top sponsor's billing credit, first thing under the hero.
                It draws nothing when no sponsor carries a heading, and
                .evb-main spaces its children with a gap, so an event without
                one is byte for byte the page it was before. The same sponsors
                are still listed in full further down. */}
            <EventTitleSponsor
              sponsors={readValue(scope, 'sponsors')}
              resolveUrl={safeUrl}
            />

            <EventFacts
              startDatetime={fmt(`${recordAlias}.start_datetime`, 'datetime')}
              endDatetime={fmt(`${recordAlias}.end_datetime`, 'datetime')}
              numberOfActivities={fmt(`${recordAlias}.number_of_activities`)}
              activityMode={fmt(`${recordAlias}.activity_mode`)}
              timeZoneLabel={timeZoneLabel}
            />


            <EventAbout
              title={fmt(`${recordAlias}.title`)}
              // description={fmt(`${recordAlias}.description`)}
              /* Raw, not formatted: the description is HTML now, and `fmt`
                 coerces a value to a display string for a text node. The
                 sanitizer in ProseParagraph is what makes it safe to render. */
              description={readValue(scope, `${recordAlias}.description`)}
              city={fmt(`${recordAlias}.location`)}
              state={fmt(`${recordAlias}.location_state`)}
              country={fmt(`${recordAlias}.location_country`)}
              pincode={fmt(`${recordAlias}.location_pincode`)}
              officialWebsiteUrl={safeUrl(readValue(scope, `${recordAlias}.official_website_url`))}
            />

            <EventCategories
              categories={activities}
              formatDistance={value => formatValue(value, 'number')}
              formatFee={value => formatAmount(value, currency)}
              formatNumber={value => formatValue(value, 'number')}
              resolveUrl={safeUrl}
            />

            {/* Both optional at creation, so each appears only when the
                organiser wrote one. */}
            <EventProseSection
              heading="Prerequisites"
              text={readValue(scope, `${recordAlias}.prerequisites`)}
            />
            <EventProseSection
              heading="How to prepare"
              text={readValue(scope, `${recordAlias}.preparation_steps`)}
            />

            {/* Shown whenever there is anything to say — the registration row
                itself, or a capacity the activities declare. This is the
                phone's only copy of the window, so hiding it on a missing
                related block left a phone with no registration details at all. */}
            {(isTruthy(registration) || capacity !== null) && (
              <EventRegistrationSection
                opensAt={opensAt}
                closesAt={closesAt}
                places={places}
                timezone={fmt(`${recordAlias}.timezone`)}
                registrationType={registrationType}
                registrationRequired={registrationRequired}
                externalUrl={externalUrl}
              />
            )}

            {isTruthy(organiser) && (
              <EventOrganiser
                name={fmt('organiser.name')}
                city={fmt('organiser.city')}
                state={fmt('organiser.state')}
                websiteUrl={organiserWebsiteUrl}
              />
            )}

            <EventSponsors
              sponsors={readValue(scope, 'sponsors')}
              resolveUrl={safeUrl}
            />

          </div>

          <EventRail
            isPaid={isPaid}
            feeAmount={feeAmount}
            opensAt={opensAt}
            closesAt={closesAt}
            places={places}
            registrationType={registrationType}
            registrationRequired={registrationRequired}
            externalUrl={externalUrl}
            buttons={buttons}
            isCompleted={isCompleted}
            pendingPayment={pendingPayment}
            onAction={onAction}
            onBookMore={onBookMore}
          />
        </div>

        <EventStickyBar
          isPaid={isPaid}
          feeAmount={feeAmount}
          buttons={buttons}
          isCompleted={isCompleted}
          pendingPayment={pendingPayment}
          onAction={onAction}
          onBookMore={onBookMore}
        />

      </div>
    </div>
  );
}
